import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Save, Clock, Lock, ShieldCheck, Eye, AlertTriangle, ShieldOff } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";

function formatTime(s) {
  const m = Math.floor(s / 60), sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
}

const VIOLATION_MSG = "Your account has been locked due to a violation of Pelico Data Work Policies. Please contact the administrator for further assistance.";

// Input-types that inject *pre-existing* content into the field. We block
// paste / drag-drop / yank (candidate must manually type) but we DO allow
// `insertReplacementText` because that's what mobile IMEs (Gboard / SwiftKey)
// fire for autocorrect + swipe-typing — blocking it breaks mobile typing.
const NON_MANUAL_INPUT_TYPES = new Set([
  "insertFromPaste",
  "insertFromPasteAsQuotation",
  "insertFromDrop",
  "insertFromYank",
]);

export default function CandidateTyping() {
  const { t, lang } = useI18n();
  const [pages, setPages] = useState([]);
  const [pageId, setPageId] = useState("");
  const [page, setPage] = useState(null);
  const [typed, setTyped] = useState([]);
  const [busy, setBusy] = useState(false);
  const [expired, setExpired] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [running, setRunning] = useState(false);
  const [reviewMode, setReviewMode] = useState(false);
  const [status, setStatus] = useState({ locked: false, is_weekend: false, weekday: "" });
  const lastSavedRef = useRef(0);

  // ---- Initial load: status + assignment + pages ----
  const loadAll = useCallback(async () => {
    try {
      const st = await api.get("/candidate/status");
      setStatus(st.data);
      if (st.data.locked) return;
      if (st.data.is_weekend) return;
      const a = await api.get("/candidate/assignment");
      setExpired(a.data.expired);
      const r = await api.get("/candidate/pages");
      setPages(r.data);
      const current = r.data.find((p) => p.status === "current");
      if (current) setPageId(current.id); else setPageId("");
    } catch (err) {
      console.error("CandidateTyping load failed:", err);
      if (err?.response?.status === 423) setStatus((sd) => ({ ...sd, locked: true }));
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ---- Global keyboard / clipboard guard listeners while typing ----
  // Every trick (paste, copy-select, drag-drop, right-click, extension autofill,
  // bot-driven synthetic events) is *silently* blocked so nothing gets typed.
  // No account lock is triggered — the trick simply produces no input.
  useEffect(() => {
    if (!page || status.locked || status.is_weekend) return;

    const onKey = (e) => {
      const k = (e.key || "").toLowerCase();
      const ctrl = e.ctrlKey || e.metaKey;
      // Silently block clipboard / select-all shortcuts anywhere on the page.
      if (ctrl && (k === "a" || k === "c" || k === "x" || k === "v")) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    const swallow = (e) => { e.preventDefault(); e.stopPropagation(); };

    document.addEventListener("keydown", onKey, true);
    document.addEventListener("copy", swallow, true);
    document.addEventListener("cut", swallow, true);
    document.addEventListener("paste", swallow, true);
    document.addEventListener("contextmenu", swallow, true);
    document.addEventListener("drop", swallow, true);
    document.addEventListener("dragover", swallow, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("copy", swallow, true);
      document.removeEventListener("cut", swallow, true);
      document.removeEventListener("paste", swallow, true);
      document.removeEventListener("contextmenu", swallow, true);
      document.removeEventListener("drop", swallow, true);
      document.removeEventListener("dragover", swallow, true);
    };
  }, [page, status.locked, status.is_weekend]);

  // ---- Page selection ----
  useEffect(() => {
    if (!pageId) { setPage(null); setRunning(false); setTyped([]); return; }
    (async () => {
      try {
        const r = await api.get(`/candidate/pages/${pageId}`);
        setPage(r.data);
        if (r.data.locked || r.data.completed) {
          setTyped(Array(r.data.fields.length).fill(""));
          setRunning(false); setElapsed(0);
          return;
        }
        try {
          const d = await api.get(`/candidate/draft/${pageId}`);
          if (d.data.typed_fields?.length === r.data.fields.length) {
            setTyped(d.data.typed_fields); setElapsed(d.data.elapsed_seconds || 0);
          } else {
            setTyped(Array(r.data.fields.length).fill("")); setElapsed(0);
          }
        } catch { setTyped(Array(r.data.fields.length).fill("")); setElapsed(0); }
        setRunning(true);
        setReviewMode(false);
      } catch (err) {
        if (err?.response?.status === 423) setStatus((s) => ({ ...s, locked: true }));
      }
    })();
  }, [pageId]);

  // ---- Timer ----
  useEffect(() => {
    if (!running || expired || status.locked || status.is_weekend) return;
    const id = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(id);
  }, [running, expired, status]);

  // ---- Auto-save ----
  useEffect(() => {
    if (!pageId || expired || page?.locked || status.locked || status.is_weekend) return;
    if (elapsed - lastSavedRef.current >= 10) {
      lastSavedRef.current = elapsed;
      api.post("/candidate/draft", { page_id: pageId, typed_fields: typed, elapsed_seconds: elapsed }).catch(() => {});
    }
  }, [elapsed, pageId, typed, expired, page, status]);

  const onSubmit = async () => {
    if (expired) { toast.error(t("page_locked")); return; }
    setBusy(true); setRunning(false);
    try {
      const r = await api.post("/candidate/submit", { page_id: pageId, typed_fields: typed, time_taken_seconds: elapsed });
      void r;
      setReviewMode(false);
      await loadAll();
      setElapsed(0);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 423) setStatus((s) => ({ ...s, locked: true }));
      else if (code === 403) toast.error(e?.response?.data?.detail || "Forbidden");
      else toast.error("Submit failed");
      setRunning(true);
    } finally { setBusy(false); }
  };

  const totalChars = typed.reduce((a, t) => a + t.length, 0);
  const liveWpm = elapsed > 0 ? Math.round((totalChars / 5) / (elapsed / 60)) : 0;
  const isLocked = page?.locked || page?.completed;
  const completedCount = pages.filter((p) => p.completed).length;
  const currentPage = pages.find((p) => p.status === "current");
  const allDone = !currentPage && completedCount > 0;

  // ---- Locked screen ----
  if (status.locked) {
    return (
      <div className="rounded-2xl bg-white border border-rose-200 p-8 sm:p-10 max-w-2xl mx-auto text-center shadow-[0_8px_30px_-10px_rgba(225,29,72,0.25)]">
        <div className="w-14 h-14 rounded-2xl bg-rose-50 text-[var(--accent)] flex items-center justify-center mx-auto"><ShieldOff size={28} /></div>
        <h2 className="font-display text-2xl font-bold mt-4">Account Locked</h2>
        <p className="text-sm text-zinc-700 mt-3 leading-relaxed">{VIOLATION_MSG}</p>
        {status.lock_reason && <div className="mt-4 text-xs text-zinc-500 font-mono">Reason: {status.lock_reason}</div>}
      </div>
    );
  }

  // ---- Weekend screen ----
  if (status.is_weekend) {
    return (
      <div className="rounded-2xl bg-white border border-[var(--line)] p-8 sm:p-10 max-w-2xl mx-auto text-center shadow-[0_8px_30px_-10px_rgba(10,10,10,0.12)]">
        <div className="w-14 h-14 rounded-2xl bg-zinc-100 text-[var(--ink)] flex items-center justify-center mx-auto"><AlertTriangle size={28} /></div>
        <h2 className="font-display text-2xl font-bold mt-4">{lang === "hi" ? "वीकेंड बंद" : "Weekend Closed"}</h2>
        <p className="text-sm text-zinc-700 mt-3 leading-relaxed">Typing work is unavailable on weekends. Please return on the next working day.</p>
        <div className="mt-4 text-xs text-zinc-400">Server day: {status.weekday}</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{t("typing")}</h1>
          <p className="text-zinc-500 mt-2 text-sm">{t("select_page_to_start")}</p>
        </div>
        <span className="chip"><ShieldCheck size={12} className="text-[var(--accent)]" /> {completedCount} / {pages.length} {lang === "hi" ? "पूर्ण" : "completed"}</span>
      </div>

      {expired && <div className="rounded-2xl bg-rose-50 border border-rose-200 p-4 text-sm text-rose-700 mb-4">{t("page_locked")}</div>}

      {allDone && (
        <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-8 text-center">
          <ShieldCheck size={36} className="mx-auto text-emerald-600" />
          <h2 className="font-display text-2xl font-bold mt-3">{lang === "hi" ? "सभी पेज पूरे!" : "All pages complete!"}</h2>
        </div>
      )}

      {pages.length > 0 && (
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-4">
          {pages.map((p) => {
            const active = p.id === pageId;
            return (
              <button key={p.id} onClick={() => setPageId(p.id)} data-testid={`page-chip-${p.page_number}`}
                className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-mono border transition-colors ${
                  active ? "bg-[var(--ink)] text-white border-[var(--ink)]"
                  : p.completed ? "bg-zinc-50 text-zinc-500 border-[var(--line)]"
                  : "bg-white border-[var(--line)] text-zinc-700"
                }`}>
                {p.page_name} {p.completed && "✓"}
              </button>
            );
          })}
        </div>
      )}

      {page && (
        <>
          {isLocked && (
            <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 mb-4 flex items-center gap-2 text-sm text-amber-800">
              <Lock size={16} /> {lang === "hi" ? "यह पेज सबमिट हो चुका है — केवल पढ़ने योग्य।" : "This page has been submitted — read-only."}
            </div>
          )}

          {!isLocked && (
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="rounded-2xl bg-white border border-[var(--line)] p-4 flex items-center gap-3 shadow-sm">
                <Clock size={18} className="text-zinc-500" />
                <div>
                  <div className="text-xs uppercase tracking-widest text-zinc-500">{t("time_taken")}</div>
                  <div className="font-mono text-lg font-bold" data-testid="live-timer">{formatTime(elapsed)}</div>
                </div>
              </div>
              <div className="rounded-2xl bg-white border border-[var(--line)] p-4 shadow-sm">
                <div className="text-xs uppercase tracking-widest text-zinc-500">{t("wpm")}</div>
                <div className="font-display text-2xl font-bold" data-testid="live-wpm">{liveWpm}</div>
              </div>
              <div className="rounded-2xl bg-white border border-[var(--line)] p-4 shadow-sm">
                <div className="text-xs uppercase tracking-widest text-zinc-500">Chars</div>
                <div className="font-display text-2xl font-bold">{totalChars}</div>
              </div>
            </div>
          )}

          {/* ============ INTERLEAVED FIELDS (reference + typing box for each field) ============ */}
          <section data-reference="1"
            onCopy={(e) => { e.preventDefault(); }}
            onCut={(e) => { e.preventDefault(); }}
            onContextMenu={(e) => { e.preventDefault(); }}
            onDragStart={(e) => { e.preventDefault(); }}
            className="rounded-2xl bg-white border border-[var(--line)] shadow-[0_1px_2px_rgba(10,10,10,0.04)] p-5 sm:p-6"
          >
            <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
              <h2 className="font-display text-lg font-bold">Typing Section</h2>
              <span className="chip"><ShieldCheck size={12} className="text-[var(--accent)]" /> Reference protected · Read-only</span>
            </div>

            <div className="space-y-6">
              {page.fields.map((orig, i) => (
                <div key={`${page.id}-pair-${i}`} className="rounded-xl border border-[var(--line)] bg-zinc-50/40 p-4 sm:p-5">
                  <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 font-semibold mb-3">
                    {t("field")} {i + 1}
                  </div>

                  {/* Encrypted reference content for this field */}
                  <div className="font-mono text-sm leading-7 bg-white border border-[var(--line)] rounded-lg p-3 break-all whitespace-pre-wrap select-none"
                    style={{ userSelect: "none", WebkitUserSelect: "none", MozUserSelect: "none", msUserSelect: "none", caretColor: "transparent" }}
                  >{orig}</div>

                  {/* Typing box directly below */}
                  {!isLocked && (
                    <textarea
                      data-testid={`type-field-${i}`}
                      rows={3}
                      value={typed[i] || ""}
                      disabled={expired}
                      autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}
                      placeholder={lang === "hi" ? "यहाँ टाइप करें…" : "Type here…"}
                      onPaste={(e) => { e.preventDefault(); }}
                      onDrop={(e) => { e.preventDefault(); }}
                      onDragOver={(e) => { e.preventDefault(); }}
                      onBeforeInput={(e) => {
                        // Only block paste / drag-drop / yank. Everything else
                        // (regular typing, mobile IME autocorrect / swipe, IME
                        // composition) must pass through so students on phones
                        // + in-app browsers can type without issues.
                        const ne = e.nativeEvent;
                        if (ne && NON_MANUAL_INPUT_TYPES.has(ne.inputType || "")) {
                          e.preventDefault();
                        }
                      }}
                      onChange={(e) => {
                        const nf = [...typed]; nf[i] = e.target.value; setTyped(nf);
                      }}
                      className="mt-3 w-full border border-[var(--line)] rounded-lg px-3 py-2.5 font-mono text-sm bg-white focus:border-[var(--ink)] focus:ring-2 focus:ring-[var(--ink)]/10 outline-none transition-colors"
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="mt-4 text-[11px] text-zinc-400 flex items-center gap-1">
              <AlertTriangle size={11} /> Please type each field manually. Copy, paste and drag-drop are not allowed.
            </div>

            {!isLocked && (
              <div className="mt-6 flex items-center justify-between flex-wrap gap-3 pt-5 border-t border-[var(--line)]">
                <div className="text-xs text-zinc-500 flex items-center gap-1"><Save size={12} /> {t("saved_draft")}</div>
                <div className="flex flex-wrap gap-3 justify-end">
                  {!reviewMode ? (
                    <button data-testid="review-btn" onClick={() => setReviewMode(true)}
                      className="px-4 py-2 rounded-full border border-[var(--line)] text-sm font-semibold inline-flex items-center gap-1 hover:bg-zinc-50">
                      <Eye size={14} /> {lang === "hi" ? "समीक्षा" : "Review"}
                    </button>
                  ) : (
                    <>
                      <button onClick={() => setReviewMode(false)} className="btn-ghost">{t("cancel")}</button>
                      <button data-testid="submit-typing" onClick={onSubmit} disabled={busy || expired}
                        className="btn-primary disabled:opacity-50">
                        {busy ? t("loading") : <>{t("submit")} <CheckCircle2 size={16} /></>}
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
